import { useCallback } from 'react';
import { useField } from 'formik';
import { Box, media, Stack } from '@koob/margaret';
import styled from 'styled-components';
import { readFile } from 'utils';
import GalleryItemField from './GalleryItemField';
import Dropzone from 'components/Dropzone';
import { FormLabel } from 'ui';

const GalleryGrid = styled(Box)`
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing()};
  grid-template-columns: repeat(2, 1fr);
  width: 100%;

  ${media.desktop`
    grid-template-columns: repeat(${({ maximumColumnsCount }) =>
      maximumColumnsCount}, 1fr);
  `}
`;

const GalleryField = ({
  name,
  label,
  maximumColumnsCount = 2,
  disabled,
  canSelectMainPicture = false,
}) => {
  const [{ value }, , { setValue }] = useField({ name });
  const [daysData, , { setValue: setDaysData }] = useField('programs');

  const handleAddFiles = async (files) => {
    const newValue = await Promise.all(
      files.map(async (file) => {
        const imageUrl = await readFile(file);
        return {
          file,
          displayName: file.name,
          imageUrl,
        };
      }),
    );

    if (canSelectMainPicture && value.length === 0) {
      newValue[0].isMainPicture = true;
    }

    setValue([...value, ...newValue]);
  };

  const handleRemoveItem = useCallback(
    (targetIndex) => {
      const copy = [];
      let currentPicture = null;

      value.forEach((item, index) => {
        index === targetIndex ? (currentPicture = item) : copy.push(item);
      });
      if (
        canSelectMainPicture &&
        value[targetIndex].isMainPicture &&
        copy.length !== 0
      ) {
        copy[0].isMainPicture = true;
      }
      const isDayPicture = daysData?.value?.find(
        (item) => item?.picture?.id === currentPicture?.id,
      );
      if (isDayPicture) {
        const newDays = daysData.value.filter(
          (item) => item.id !== isDayPicture.id,
        );
        delete isDayPicture?.picture;
        setDaysData([...newDays, ...[isDayPicture]]);
      }
      setValue(copy);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue, value],
  );

  const selectMainPicture = useCallback(
    (targetIndex) => {
      const copy = [...value];
      copy.forEach((value, index) => {
        value.isMainPicture = index === targetIndex;
      });
      setValue(copy);
    },
    [setValue, value],
  );

  return (
    <Stack size="full" direction="column">
      {Boolean(label) && <FormLabel>{label}</FormLabel>}
      <Stack size="full" gutterSize={0.5}>
        <GalleryGrid maximumColumnsCount={maximumColumnsCount}>
          {value.map((item, index) => (
            <GalleryItemField
              key={index}
              index={index}
              item={item}
              name={`${name}.${index}`}
              onDelete={handleRemoveItem}
              disabled={disabled}
              onSelectMainPicture={selectMainPicture}
              canSelectMainPicture={canSelectMainPicture && !item.isDayPicture}
            />
          ))}
          {!disabled && <Dropzone onDrop={handleAddFiles} />}
        </GalleryGrid>
      </Stack>
    </Stack>
  );
};

export default GalleryField;
